import React from 'react';
import { useSearchParams } from 'react-router';
import { CreditCard, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/auth-context';
import { toast } from 'sonner';
import type {
	OrgSubscriptionData,
	OrgUsageData,
	OrgInvoicesData,
	PlanTier,
} from '@/api-types';
import { PLAN_DEFINITIONS } from '@/api-types';
import { UsageSummary } from './UsageSummary';
import { InvoiceHistory } from './InvoiceHistory';
import { PlanChangeDialog } from './PlanChangeDialog';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';

export function BillingSection() {
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();
	const [orgId, setOrgId] = React.useState<string | null>(null);
	const [subscription, setSubscription] = React.useState<OrgSubscriptionData | null>(null);
	const [usage, setUsage] = React.useState<OrgUsageData | null>(null);
	const [invoices, setInvoices] = React.useState<OrgInvoicesData | null>(null);
	const [loading, setLoading] = React.useState(true);
	const [changePlanOpen, setChangePlanOpen] = React.useState(false);
	const [cancelOpen, setCancelOpen] = React.useState(false);
	const [targetTier, setTargetTier] = React.useState<PlanTier>('pro');

	const loadBillingData = React.useCallback(async (oId: string) => {
		try {
			const [subRes, usageRes, invRes] = await Promise.all([
				apiClient.getOrgSubscription(oId),
				apiClient.getOrgUsage(oId),
				apiClient.getOrgInvoices(oId),
			]);

			if (subRes.success && subRes.data) setSubscription(subRes.data);
			if (usageRes.success && usageRes.data) setUsage(usageRes.data);
			if (invRes.success && invRes.data) setInvoices(invRes.data);
		} catch (error) {
			console.error('Failed to load billing data', error);
		} finally {
			setLoading(false);
		}
	}, []);

	React.useEffect(() => {
		const checkoutStatus = searchParams.get('checkout');
		if (checkoutStatus) {
			if (checkoutStatus === 'success') {
				toast.success('Checkout completed successfully! Your plan is now active.');
			} else if (checkoutStatus === 'canceled') {
				toast.info('Checkout was canceled. No changes were made.');
			}
			searchParams.delete('checkout');
			setSearchParams(searchParams, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	React.useEffect(() => {
		if (!user) return;

		apiClient.getUserOrgs().then((res) => {
			if (res.success && res.data?.orgs?.length) {
				const id = res.data.orgs[0].id;
				setOrgId(id);
				loadBillingData(id);
			} else {
				setLoading(false);
			}
		}).catch(() => setLoading(false));
	}, [user, loadBillingData]);

	if (!user) return null;

	const currentTier = (subscription?.plan?.tier as PlanTier) || 'free';
	const currentPlan = PLAN_DEFINITIONS[currentTier];
	const sub = subscription?.subscription;
	const isCancelPending = sub?.cancel_at_period_end;

	const handleManageBilling = async () => {
		if (!orgId) return;
		try {
			const res = await apiClient.createPortalSession(orgId);
			if (res.success && res.data?.url) {
				window.location.href = res.data.url;
			} else {
				toast.error('Failed to open billing portal');
			}
		} catch {
			toast.error('Failed to open billing portal');
		}
	};

	const handleReactivate = async () => {
		if (!orgId) return;
		try {
			const res = await apiClient.reactivateSubscription(orgId);
			if (res.success) {
				toast.success('Subscription reactivated');
				loadBillingData(orgId);
			}
		} catch {
			toast.error('Failed to reactivate');
		}
	};

	const handleUpgrade = (tier: PlanTier) => {
		setTargetTier(tier);
		setChangePlanOpen(true);
	};

	return (
		<>
			<Card id="billing">
				<CardHeader variant="minimal">
					<div className="flex items-center gap-3 border-b w-full py-3 text-text-primary">
						<CreditCard className="h-5 w-5" />
						<div className="flex-1">
							<CardTitle>Billing & Subscription</CardTitle>
						</div>
						{sub?.stripe_customer_id && (
							<Button variant="outline" size="sm" onClick={handleManageBilling} className="gap-2">
								<ExternalLink className="h-4 w-4" />
								Manage Billing
							</Button>
						)}
					</div>
				</CardHeader>
				<CardContent className="space-y-6 px-6 mt-4">
					{loading ? (
						<p className="text-sm text-text-tertiary">Loading billing information...</p>
					) : !orgId ? (
						<p className="text-sm text-text-tertiary">
							Create an organization to manage billing.
						</p>
					) : (
						<>
							{/* Current Plan */}
							<div className="flex items-center justify-between">
								<div>
									<h4 className="font-medium">Current Plan</h4>
									<div className="flex items-center gap-2 mt-1">
										<span className="text-lg font-bold">{currentPlan.name}</span>
										{currentPlan.priceCents !== null && currentPlan.priceCents > 0 && (
											<span className="text-text-secondary text-sm">
												${(currentPlan.priceCents / 100).toFixed(0)}/{currentPlan.isPerSeat ? 'seat/' : ''}mo
											</span>
										)}
										{isCancelPending && (
											<Badge variant="destructive">Canceling</Badge>
										)}
										{sub?.status === 'past_due' && (
											<Badge variant="destructive">Past Due</Badge>
										)}
									</div>
									{sub?.current_period_end && (
										<p className="text-sm text-text-tertiary mt-1">
											{isCancelPending ? 'Access until' : 'Renews'}{' '}
											{new Date(sub.current_period_end * 1000).toLocaleDateString()}
										</p>
									)}
								</div>
								<div className="flex gap-2">
									{isCancelPending ? (
										<Button variant="outline" size="sm" onClick={handleReactivate}>
											Reactivate
										</Button>
									) : currentTier !== 'enterprise' ? (
										<>
											{currentTier !== 'team' && (
												<Button
													size="sm"
													onClick={() => handleUpgrade(currentTier === 'free' ? 'pro' : 'team')}
												>
													Upgrade
												</Button>
											)}
											{currentTier !== 'free' && (
												<Button
													variant="outline"
													size="sm"
													onClick={() => setCancelOpen(true)}
												>
													Cancel Plan
												</Button>
											)}
										</>
									) : null}
								</div>
							</div>

							<Separator />

							{/* Usage Summary */}
							<div>
								<h4 className="font-medium mb-3">Usage This Period</h4>
								<UsageSummary usage={usage?.usage || []} />
							</div>

							<Separator />

							{/* Invoice History */}
							<div>
								<h4 className="font-medium mb-3">Invoice History</h4>
								<InvoiceHistory invoices={invoices?.invoices || []} />
							</div>
						</>
					)}
				</CardContent>
			</Card>

			{orgId && (
				<>
					<PlanChangeDialog
						open={changePlanOpen}
						onOpenChange={setChangePlanOpen}
						orgId={orgId}
						currentTier={currentTier}
						targetTier={targetTier}
						onSuccess={() => loadBillingData(orgId)}
					/>
					<CancelSubscriptionDialog
						open={cancelOpen}
						onOpenChange={setCancelOpen}
						orgId={orgId}
						onSuccess={() => loadBillingData(orgId)}
					/>
				</>
			)}
		</>
	);
}
